import Model from "../../model/Model";
import Review, {ReviewStatus} from "../../model/Review";
import User from "../../model/User";
import * as React from "react";
import {ReactElement, useEffect, useState} from "react";
import {useResources} from "../../stores/ResourceProvider";
import {
  Avatar,
  DialogContentText,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  Switch,
  useTheme
} from "@mui/material";
import DatePickerValidator from "../../components/form/DatePickerValidator";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import TextFieldValidator from "../../components/form/TextFieldValidator";
import Snapshot from "../../model/Snapshot";
import {
  dateToLocalFormat,
  getISODateFromDate,
  getISODateToday,
  humanizeString,
  isoToLocalDate
} from "../../stores/StoreUtilities";
import config from 'react-global-configuration';
import {CopyToClipboard} from 'react-copy-to-clipboard';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {SnapshotType, UpdateReviewInput} from "../../API";
import ModelEditDialog from "../../components/model/ModelEditDialog";

const EditReviewDialog = ({
  review,
  onSave,
  onClose,
  onDelete
}: {
  review: Review,
  onSave(review: Review): void
  onClose(): void
  onDelete?(review: Review): void
}) => {
  const [client, setClient] = useState<User | undefined>()
  const [reviewDate, setReviewDate] = useState<Date | undefined>(new Date())
  const [reviewModel, setReviewModel] = useState<Model | undefined>()
  const [reviewSnapshot, setReviewSnapshot] = useState<Snapshot | undefined>()
  const [models, setModels] = useState<Model[]>([])
  const [snapshots, setSnapshots] = useState<Snapshot[]>([])
  const [baseSnapshot, setBaseSnapshot] = useState<Snapshot | undefined>()
  const [managed, setManaged] = useState<boolean>(false)
  const [reviewLink, setRevierwLink] =
    useState<string | undefined>(`${config.get("baseUrl")}/review/${review.id}`)
  const [reviewStatus, setReviewStatus] = useState<ReviewStatus>(ReviewStatus.Pending)
  const [isProcessing, setIsProcessing] = useState<boolean>(false)

  const {modelStore, userStore, notify} = useResources()
  const theme = useTheme()
  
  const styles = {
    formLabel: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: "1.0em",
      maxHeight: 15,
      marginBottom: "6px",
      textWrap: "nowrap",
      color: theme.palette.grey["700"]
    },
  }

  useEffect(() => {
    if (review) {
      loadReview(review)
    }
  }, [review])

  const loadReview = async (review: Review) => {
    setReviewDate(isoToLocalDate(review.reviewDate))
    let user: User | undefined
    if (review.userId) {
      user = await userStore.getUser(review.userId)
      if (user) {
        setClient(user)
        setModels(user.models.sort((a: Model, b: Model) => a.name.localeCompare(b.name)))
      }
    }
    if (user && review.modelId) {
      let model = user.models.find((m: Model) => m.id === review.modelId)
      if (model) {
        model = await loadModel(model, review.snapshotId, review.baseSnapId)
        setReviewModel(model)
      }
    }
    setManaged(review.managed)
    setReviewStatus(review.status ?? ReviewStatus.Pending)
  }

  const loadModel = async (model: Model, snapshotId?: string, baseSnapId?: string) => {
    const fullModel = await modelStore.getCurrentModel(model.id, true)
    if (fullModel) {
      setReviewModel(fullModel)
      setSnapshots(fullModel.snapshots)
      if (snapshotId) {
        const snapshot = fullModel.snapshots.find((s: Snapshot) => s.id === review.snapshotId)
        setReviewSnapshot(snapshot)
      } else {
        setReviewSnapshot(undefined)
      }
      if (baseSnapId) {
        const snapshot = fullModel.snapshots.find((s: Snapshot) => s.id === review.baseSnapId)
        setBaseSnapshot(snapshot)
        if (!snapshot && fullModel.snapshots.length > 0) {
          setBaseSnapshot(fullModel.snapshots[0])
        }
      } else {
        if (fullModel.snapshots.length > 0) {
          setBaseSnapshot(fullModel.snapshots[0])
        }
      }
    }
    return fullModel
  }

  const handleSubmit = async () => {
    try {
      setIsProcessing(true)

      if (review && reviewModel && baseSnapshot && reviewSnapshot && reviewDate) {
        const baseSnap = await modelStore.updateSnapshotBudget(baseSnapshot, reviewModel)
        const input: UpdateReviewInput = {
          id: review.id,
          userId: reviewModel.userId,
          accountId: reviewModel.accountId,
          modelId: reviewModel.id,
          baseSnapId: baseSnapshot.id,
          snapshotId: reviewSnapshot.id,
          reviewDate: getISODateFromDate(reviewDate),
          managed: managed,
          status: reviewStatus
        }

        if (reviewStatus === ReviewStatus.Submitted) {
          if (!review.submittedOn) {
            input.submittedOn = getISODateToday()
          }
        } else {
          input.submittedOn = null
        }

        const update = await userStore.updateReview(input)
        if (update) {
          onSave(update)
        }
      }
    } catch (err: any) {
      notify.show("error", `Error saving review (${err.message})`)
    } finally {
      setIsProcessing(false)
    }
  }

  const handleDelete = async () => {
    const deleted = await userStore.deleteReview(review.id)
    if (deleted && onDelete) {
      onDelete(deleted)
    }
  }

  const onCopyLink = () => {
    notify!.show('success', 'Copied Review link to clipboard.')
  }

  const renderAvatar = () => {
    let avatar: ReactElement | undefined
    if (client) {
      const bgcolor = (client.id === userStore.user?.id) ? theme.palette.primary.main : theme.palette.action.active
      avatar = <Avatar sx={{bgcolor: bgcolor, fontSize:13, width:25, height:25}}>
        {client.initials}
      </Avatar>
    } else {
      avatar = <ManageAccountsIcon/>
    }
    return avatar
  }

  return (
    <ModelEditDialog title="Edit Review" open={true} size="sm"
                     onCancel={onClose}
                     onSave={handleSubmit}
                     onDelete={onDelete ? handleDelete : undefined}
    >
      <DialogContentText>
        You may change the review date or base it on a different model and snapshots.
      </DialogContentText>
      <Grid container spacing={1} mt={0.5}>
          <Grid item xs={8}>
            <TextFieldValidator
              margin="dense"
              name="client"
              label="Client"
              type="text"
              validators={{required: true}}
              value={client ? client.fullName : null}
              disabled={true}
              fullWidth
              variant="standard"
              InputLabelProps={{shrink: true}}
            />
          </Grid>
          <Grid item xs={4}>
            <DatePickerValidator
              name="reviewDate"
              label="Review Date"
              value={reviewDate}
              format="MM-dd-yyyy"
              variant="standard"
              margin="dense"
              fullWidth
              required
              onChange={(newValue: any) => {
                setReviewDate(newValue)
              }}
            />
          </Grid>
          <Grid item xs={8}>
            <TextFieldValidator
                type="text"
                validators={{ required: true }}
                name="ReviewModel"
                label="Review Model"
                variant="standard"
                autocompleteOptions={{
                  freeSolo: false,
                  options: models,
                  getOptionLabel: (option: Model) => option && option.name ? option.name : "None",
                  isOptionEqualToValue: (option: Model, value: Model) => {
                    return (option.id === value.id)
                  },
                  value: reviewModel ?? null,
                  onChange: (event: any, value: Model, reason: any) => {
                    loadModel(value)
                  }
                }}
            />
          </Grid>
          <Grid item xs={4} sx={{display:"flex", flexDirection:"column", justify:"bottom"}}>
            <TextFieldValidator
              type="text"
              validators={{ required: true }}
              name="reviewSnapshot"
              label="Review Snapshot"
              variant="standard"
              autocompleteOptions={{
                freeSolo: false,
                options: snapshots,
                getOptionLabel: (option: Snapshot) => option && option.date ? `${dateToLocalFormat(option.date)}${option.snapshotType === SnapshotType.Combined ? "*" : ""}` : "None",
                isOptionEqualToValue: (option: Snapshot, value: Snapshot) => {
                  return (option.id === value.id)
                },
                value: reviewSnapshot ?? null,
                onChange: (event: any, value: Snapshot, reason: any) => {
                  setReviewSnapshot(value)
                }
              }}
            />
          </Grid>
          <Grid item xs={8}>
          </Grid>
          <Grid item xs={4}>
            <TextFieldValidator
                type="text"
                validators={{ required: true }}
                name="baseSnapshot"
                label="Base Snapshot"
                variant="standard"
                autocompleteOptions={{
                  freeSolo: false,
                  options: snapshots,
                  getOptionLabel: (option: Snapshot) => option && option.date ? `${dateToLocalFormat(option.date)}${option.snapshotType === SnapshotType.Combined ? "*" : ""}` : "None",
                  isOptionEqualToValue: (option: Snapshot, value: Snapshot) => {
                    return (option.id === value.id)
                  },
                  value: baseSnapshot ?? null,
                  onChange: (event: any, value: Snapshot, reason: any) => {
                    setBaseSnapshot(value)
                  }
                }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextFieldValidator
              margin="dense"
              name="reviewLink"
              label="Review Link"
              type="text"
              validators={{required:true}}
              value={reviewLink}
              disabled={true}
              fullWidth
              variant="standard"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <CopyToClipboard
                      text={reviewLink ?? ""}
                      onCopy={onCopyLink}>
                        <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => {/* TODO */}}
                      >
                        <ContentCopyIcon/>
                      </IconButton>
                    </CopyToClipboard>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextFieldValidator
              type="text"
              validators={{ required: true }}
              required
              name="reviewStatus"
              label="Review Status"
              variant="standard"
              styleProp={{width:"300px"}}
              autocompleteOptions={{
                freeSolo: false,
                disableClearable: true,
                value: reviewStatus,
                options: Object.values(ReviewStatus),
                getOptionLabel: (option: string) => option ? humanizeString(option) : null,
                onChange: (event: any, value: ReviewStatus, reason: any) => {
                  setReviewStatus(value)
                }
              }}
            />
          </Grid>
        <Grid item xs={12}>
          <FormControlLabel control={
            <Switch checked={managed}
                    onChange={(event: any) => {
                      setManaged(event.target.checked)
                    }}
            />}
            label="Hide Managed Assets from Clients"
          />
        </Grid>
      </Grid>
    </ModelEditDialog>
  )
}

export default EditReviewDialog